import styled, { css } from 'styled-components';
import Link from 'next/link';
import { useEffect, useRef } from 'react';

import { BRAND_VIEW_SECTION_TYPES } from 'homepage/enums/contentConstants';
import { SpaceFillingVideo } from 'shared/components/SpaceFillingVideo';
import { IFeedHeroSection } from 'homepage/types/Feed';
import { Image } from 'shared/components/Image';
import { ORIENTATION_STYLES, NUM_OF_COLUMNS } from 'homepage/styles/constants';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

interface IBrandedContent extends IFeedHeroSection {
  canvasHeight?: number;
  canvasWidth?: number;
  disableLazyLoad?: boolean;
  drawerName?: string;
  firstVideo: number;
  setFirstVideo: (value: number) => void;
  theme?: 'light' | 'dark';
}

export const BrandedContent = ({
  buttonLink = null,
  canvasHeight = 400,
  canvasWidth = 400,
  disableLazyLoad = false,
  drawerName = '',
  firstVideo,
  id,
  index,
  subtitle,
  theme = 'light',
  title,
  titleOrientation: assetOrientation,
  type,
  pictures = [],
  setFirstVideo,
  uuid,
  videos = [],
}: IBrandedContent) => {
  const asset = pictures?.[0] || videos?.[0];
  const contentRef = useRef();
  const isContentInViewport = useIsElementInViewport(contentRef);
  const { windowSize } = useWindowSize();
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  useEffect(() => {
    if (videos?.[0] && (firstVideo === null || firstVideo > index)) {
      setFirstVideo(index);
    }
  }, []);

  if (!asset) return null;

  const { aspect, url } = asset;

  // some checks to create structured randomness being requested
  const isHalf = title?.toLowerCase().includes('half');
  const isLandscape = aspect > 1.1;
  const isLong = aspect < 0.6;
  const isPortrait = aspect < 1;
  const isSquare = aspect >= 1 && aspect <= 1.1;
  const isSuperWide = aspect > 2;
  const displayWide = title?.toLowerCase().includes('wide');
  const shouldOverlap = subtitle?.toLowerCase().includes('overlap');
  const hasPadding = subtitle?.toLowerCase().includes('padding');
  const showBig = isPortrait && (typeof index === 'number' && index + 1) % NUM_OF_COLUMNS === 2;
  const showLittleLandscape =
    isSuperWide && (typeof index === 'number' && index + 1) % NUM_OF_COLUMNS === 4;

  const getImageSize = () => {
    if (displayWide) return '100vw';
    if (isHalf) return '45vw';
    if (isPortrait) return '25vw';
    return '30vw';
  };

  const generateHeightAndWdith = () => {
    if (displayWide) {
      return {
        height: `${100 / aspect}vw`,
        width: `100vw`,
      };
    }
    if (isHalf) {
      return {
        height: `${45 / aspect}vw`,
        width: `45vw`,
      };
    }
    if (isSuperWide) {
      return {
        height: `${50 / aspect}vw`,
        width: `50vw`,
      };
    }
    if (isPortrait && showBig) {
      return {
        height: `${30 / aspect}vw`,
        width: `30vw`,
      };
    }

    if (isPortrait) {
      return {
        height: `${140 / aspect}px`,
        width: `140px`,
      };
    }

    return {
      height: `${25 / aspect}vw`,
      width: `25vw`,
    };
  };

  const imageSize = getImageSize();
  const heightAndWidth = generateHeightAndWdith();

  const trackContentClick = () => {
    sendTrackingEvent('HOMEPAGE_LANDING_TAP', {
      drawer_name: drawerName,
      target_destination: buttonLink,
      service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
    });
  };

  const content =
    type === BRAND_VIEW_SECTION_TYPES.HERO_SECTION ? (
      <BrandedContent.Image
        $assetPositioning={ORIENTATION_STYLES[assetOrientation]}
        data-qa="homepage_brand_image"
        disableLazyLoad={isContentInViewport || disableLazyLoad}
        $displayWide={displayWide}
        $isHalf={isHalf}
        $isPortrait={isPortrait}
        $showBig={showBig}
        alt="homepage_collage"
        key={uuid || id}
        src={url}
        sizes={imageSize}
      />
    ) : (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <SpaceFillingVideo
        height={`${canvasHeight}px`}
        width={`${canvasWidth}`}
        id={uuid || id}
        url={url}
        theme={theme}
      />
    );

  return buttonLink ? (
    <Link href={`/${buttonLink}`} passHref prefetch={false} legacyBehavior>
      <BrandedContent.ContainerLink
        $aspectRatio={aspect}
        $backgroundImage={type === BRAND_VIEW_SECTION_TYPES.HERO_SECTION && url}
        data-qa="branded_content_link_item"
        $displayWide={displayWide}
        $hasPadding={hasPadding}
        key={uuid || id}
        $height={heightAndWidth?.height}
        $isHalf={isHalf}
        $isLandscape={isLandscape}
        $isLong={isLong}
        $isPortrait={isPortrait}
        $isSquare={isSquare}
        $isSuperWide={isSuperWide}
        onClick={trackContentClick}
        ref={contentRef}
        $shouldOverlap={shouldOverlap}
        $showLittleLandscape={showLittleLandscape}
        $width={heightAndWidth?.width}
      >
        {content}
      </BrandedContent.ContainerLink>
    </Link>
  ) : (
    <BrandedContent.ContainerVideo
      data-qa="branded_content_item"
      $height={windowSize?.height || canvasHeight}
      $width={windowSize?.width || canvasWidth}
    >
      {content}
    </BrandedContent.ContainerVideo>
  );
};

BrandedContent.Container = styled.div<{
  $aspectRatio: number;
  $backgroundImage: string | boolean;
  $displayWide: boolean;
  $hasPadding: boolean;
  $height: string;
  $isHalf: boolean;
  $isLandscape: boolean;
  $isLong: boolean;
  $isPortrait: boolean;
  $isSquare: boolean;
  $isSuperWide: boolean;
  $showLittleLandscape: boolean;
  $shouldOverlap: boolean;
  $width: string;
}>`
  position: relative;
  overflow: visible;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};

  ${({ $isSuperWide, $displayWide }) =>
    $isSuperWide &&
    !$displayWide &&
    css`
      grid-column: span 2;
    `}

  ${({ $isLong, $displayWide }) =>
    $isLong &&
    !$displayWide &&
    css`
      grid-row: span 2;
    `}

    ${({ $showLittleLandscape, $displayWide }) =>
    $showLittleLandscape &&
    !$displayWide &&
    css`
      grid-column: span 1;
    `}

    ${({
    $aspectRatio,
    $backgroundImage,
    $displayWide,
    $hasPadding,
    $isLandscape,
    $shouldOverlap,
    theme,
  }) =>
    $displayWide &&
    css`
      grid-column: span 4;
      aspect-ratio: ${$aspectRatio};
      ${$shouldOverlap &&
      `
        margin-bottom: -30%;
        z-index: ${theme.zIndex.homepageBrandContent};
      `}
      margin-top: 30px;
      ${$hasPadding && `margin: 32px 16px;`}
      ${$isLandscape &&
      `@media (max-width: ${theme.breakpoints.mediumSmall}px) {
        width: 100vw;
        height: 124vw;
        ${
          $backgroundImage &&
          `
           background-image: url(${$backgroundImage as string});
           background-repeat: no-repeat;
           background-position: center;
           background-size: cover;

        img {
          visibility: hidden;
        }
        `
        }
      };`}
    `}

    ${({ $isHalf }) =>
    $isHalf &&
    css`
      grid-column: span 2;
      margin-top: 30px;
      width: 45vw;
      margin: 8px 8px;
    `}

    @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $displayWide, $hasPadding }) =>
      $displayWide &&
      $hasPadding &&
      css`
        margin: 300px 149px;
      `}
  }

  ${hideScrollbars}
`;

BrandedContent.ContainerVideo = styled.div<{
  $height: number;
  $width: number;
}>`
  position: relative;
  overflow: visible;
  height: ${({ $height }) => $height - 50}px;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: ${({ $height }) => $height - 50}px;
    max-height: ${({ $height }) => $height - 50}px;
    min-height: ${({ $height }) => $height - 50}px;
  }
`;

BrandedContent.ContainerLink = styled.a<{
  $aspectRatio: number;
  $backgroundImage: string | boolean;
  $displayWide: boolean;
  $hasPadding: boolean;
  $height: string;
  $isHalf: boolean;
  $isLandscape: boolean;
  $isLong: boolean;
  $isPortrait: boolean;
  $isSquare: boolean;
  $isSuperWide: boolean;
  $shouldOverlap: boolean;
  $showLittleLandscape: boolean;
  $width: string;
}>`
  position: relative;
  overflow: visible;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};

  ${({ $isSuperWide, $displayWide }) =>
    $isSuperWide &&
    !$displayWide &&
    css`
      grid-column: span 2;
    `}

  ${({ $isLong, $displayWide }) =>
    $isLong &&
    !$displayWide &&
    css`
      grid-row: span 2;
    `}

    ${({ $showLittleLandscape, $displayWide }) =>
    $showLittleLandscape &&
    !$displayWide &&
    css`
      grid-column: span 1;
    `}

    ${({
    $displayWide,
    $aspectRatio,
    $backgroundImage,
    $hasPadding,
    $isLandscape,
    $shouldOverlap,
    theme,
  }) =>
    $displayWide &&
    css`
      grid-column: span 4;
      aspect-ratio: ${$aspectRatio};
      ${$shouldOverlap &&
      `
        margin-bottom: -30%;
        z-index: ${theme.zIndex.homepageBrandContent};
      `}
      ${$hasPadding && `margin: 32px 16px;`}
      ${$isLandscape &&
      `@media (max-width: ${theme.breakpoints.mediumSmall}px) {
        width: 100vw;
        height: 124vw;
        ${
          $backgroundImage &&
          `
           background-image: url(${$backgroundImage as string});
           background-repeat: no-repeat;
           background-position: center;
           background-size: cover;

        img {
          visibility: hidden;
        }
        `
        }
      };`}
    `}

    ${({ $isHalf }) =>
    $isHalf &&
    css`
      grid-column: span 2;
      margin-top: 30px;
      width: 45vw;
      margin: 8px 8px;
    `}

    @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $displayWide, $hasPadding }) =>
      $displayWide &&
      $hasPadding &&
      css`
        margin: 300px 149px;
      `}
  }

  ${hideScrollbars}
`;

BrandedContent.Image = styled(Image)<{
  $displayWide: boolean;
  $isHalf: boolean;
  $isPortrait: boolean;
  $assetPositioning: string;
  $showBig: boolean;
}>`
  position: absolute;

  ${({ $isHalf, $isPortrait }) =>
    $isPortrait &&
    !$isHalf &&
    css`
      width: 140px !important;
    `}

  ${({ $showBig, $isPortrait }) =>
    $isPortrait &&
    $showBig &&
    css`
      min-width: 30vw !important;
    `}

    ${({ $displayWide }) =>
    $displayWide &&
    css`
      width: 100% !important;
    `}

  ${({ $assetPositioning }) => css`
    ${$assetPositioning}
  `}
`;

BrandedContent.Video = styled.video<{
  $assetPositioning: string;
  $displayWide: boolean;
  $isPortrait: boolean;
  $showBig: boolean;
}>`
  position: absolute;

  &::-internal-media-controls-overlay-cast-button {
    display: none;
  }

  ${({ $assetPositioning }) => css`
    ${$assetPositioning}
  `}
`;

BrandedContent.InteractableVideo = styled.video`
  &::-internal-media-controls-overlay-cast-button {
    display: none;
  }
`;
